/**
 * PricingSection Organism Component
 * Pricing plans display with optional title, pricing cards and a footnote
 * (used for the sales-assisted tier that has no self-serve checkout).
 */

import React from "react";
import { storyblokEditable, StoryblokComponent } from "@/lib/blocks";
import { Container } from "@/components/atoms/Container";
import { Heading } from "@/components/atoms/Heading";
import { Text } from "@/components/atoms/Text";
import type { PricingSectionBlok } from "@/types";

export interface PricingSectionProps {
  blok: PricingSectionBlok;
}

export const PricingSection: React.FC<PricingSectionProps> = ({ blok }) => {
  const { section_title, pricing_cards, footnote, background_color } = blok;

  const bgColors: Record<string, string> = {
    primary: "bg-primary",
    secondary: "bg-secondary",
    black: "bg-black",
    "gray-800": "bg-gray-800",
    "gray-700": "bg-gray-700",
    white: "bg-white",
  };

  // Dark backgrounds need light text for the title/footnote.
  const isDark = ["black", "gray-800", "gray-700"].includes(background_color);
  const proseColor = isDark ? "text-white" : "text-black";

  const cardCount = pricing_cards?.length ?? 0;
  // One column on mobile; from lg on, one column per plan (capped at 3 so a
  // four-plan setup wraps instead of squeezing).
  const gridCols =
    cardCount >= 3
      ? "md:grid-cols-2 lg:grid-cols-3"
      : cardCount === 2
        ? "lg:grid-cols-2"
        : "lg:grid-cols-1";

  return (
    <section
      {...storyblokEditable(blok)}
      id="prices"
      className={`${
        bgColors[background_color] || "bg-primary"
      } py-12 md:py-16 lg:py-20 xl:py-24 scroll-mt-24`}
    >
      <Container>
        {section_title && (
          <Heading level="h2" className={`${proseColor} mb-8 md:mb-10 lg:mb-12`}>
            {section_title}
          </Heading>
        )}

        <div
          className={`grid grid-cols-1 ${gridCols} gap-6 md:gap-8 lg:gap-10 w-full items-stretch`}
        >
          {pricing_cards &&
            pricing_cards.map((card) => (
              <StoryblokComponent key={card._uid} blok={card} />
            ))}
        </div>

        {footnote && (
          <Text className={`${proseColor} mt-8 md:mt-10 text-center opacity-80`}>
            {footnote}
          </Text>
        )}
      </Container>
    </section>
  );
};

export default PricingSection;
